import React, { useState, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  AppBar,
  TextField,
  Divider,
  Card,
  Stack,
  Chip,
  keyframes,
  useTheme,
  InputAdornment,
  IconButton
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useMutation, useQuery, useQueryClient } from "react-query";
import client from "../../../API";
import useAlertDialog from "../../../Components/useAlertDialog";
import AlertDialog from "../../../Components/AlertDialog";
import ChatScreen from "../../../Components/ChatScreen";
import TabPanel from "../../../Components/TabPanel";
import AgencyNegotiateDialog from "./agencyNegotiate";
import { formatLinksInMessage } from "../../../Utils/constants";
import { Business, Chat, Description, AttachMoney, Warning, CheckCircle, Cancel, SwapHoriz } from "@mui/icons-material";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-5px); }
  to { opacity: 1; transform: translateY(0); }
`;

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[4],
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: theme.shadows[6]
  }
}));

function formatIdealDueDate(dateString) {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return "Invalid Date";
  } else {
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const year = date.getFullYear().toString().substr(-2);
    return `${month}/${day}/${year}`;
  }
}
const AgencyCampaignPitchDialog = ({
  openDialog,
  handleCloseDialog,
  campaign,
  username,
}) => {
  const theme = useTheme();
  const [tabIndex, setTabIndex] = useState(0);
  const [openNegotiateDialog, setOpenNegotiateDialog] = useState(false);
  const queryClient = useQueryClient();
  const { dialogState, openDialog: openAlert, closeDialog } = useAlertDialog();

  const {
    data: conversation,
    isLoading: isConversationLoading,
    refetch: refetchConversation,
  } = useQuery(
    ["conversation", campaign?.id, username],
    () =>
      client.conversations.checkCampaign({
        campaign_id: campaign?.id,
        creator_id: username,
      }),
    {
      enabled: !!campaign && !!username && openDialog,
      refetchOnWindowFocus: false,
      onError: (err) => {
        console.error("Error fetching conversation:", err);
      },
    }
  );

  const creator = useMemo(() => {
    if (!campaign) return undefined;
    return campaign.creators.find((creator) => creator.id === username);
  }, [campaign, username]);

  const currentRate = useMemo(
    () => creator?.agencyRate || creator?.price || 0,
    [creator]
  );

  const videoList = creator?.videos.filter(video => video.parentId == null).map(video => ({
    ...video
  })) || [];

  const crosspostList = creator?.videos.filter(video => video.parentId != null).map(video => ({
    ...video
  })) || [];

  const { mutate: updateStatus, isLoading: isUpdatingStatus } = useMutation(
    (input) => client.agency.campaignAccept(campaign.id, input),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["creators", username]);
        queryClient.invalidateQueries(["campaigns"]);
      },
      onError: (error) => {
        openAlert(
          "Error",
          `Error updating status: ${error.response?.data?.error || error.message}`,
          closeDialog,
          null,
          "Ok",
          null
        );
      },
    }
  );

  const handleChangeTab = (event, newValue) => {
    setTabIndex(newValue);
    if (newValue === 1 && conversation?.conversation_id) {
      queryClient.invalidateQueries(["messages", conversation.conversation_id]);
    }
  };

  const handleChangeStatus = (accepted) => {
    updateStatus(
      { status: accepted ? "Accepted" : "Declined", creator: username },
      {
        onSuccess: () => {
          const title = accepted ? "Campaign Accepted" : "Campaign Rejected";
          const message = accepted
            ? "You have successfully accepted this campaign."
            : "You have rejected this campaign.";
          openAlert(title, message, () => {
            closeDialog();
            handleCloseDialog();
          }, null, "Ok", null);
        },
      }
    );
  };

  const onClickOption = (accepted = false) => {
    const title = accepted ? "Accept Campaign" : "Reject Campaign";
    const message = accepted
      ? "By accepting this campaign, your creator will be notified of upcoming due dates and receive messages from the campaign manager. Continue?"
      : "Rejecting this campaign is irreversible and will remove it from your dashboard. Continue?";
    openAlert(title, message, () => {
      closeDialog();
      handleChangeStatus(accepted);
    }, closeDialog, "Continue", "Cancel");
  };

  if (!campaign) {
    return null;
  }
  
  return (
    <>
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="xl"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: { 
            minHeight: "90vh",
            borderRadius: 4,
            boxShadow: theme.shadows[10],
            border: `1px solid ${theme.palette.divider}`
          },
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <StyledPaper sx={{ 
            background: `linear-gradient(45deg, ${theme.palette.info.dark}, ${theme.palette.info.main})`,
            color: theme.palette.common.white,
            borderRadius: '16px 16px 0 0',
            boxShadow: 'none'
          }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Business fontSize="large" />
              <Typography variant="h3" fontWeight="700">
                {campaign.name}
              </Typography>
              <Chip 
                label="Pitch Review" 
                color="secondary" 
                sx={{ 
                  ml: 2, 
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  height: 32
                }}
              />
            </Stack>
          </StyledPaper>
        </DialogTitle>

        <AppBar 
          position="static" 
          color="transparent" 
          sx={{ 
            boxShadow: 'none',
            borderBottom: `1px solid ${theme.palette.divider}`,
            mx: 3
          }}
        >
          <Tabs
            value={tabIndex}
            onChange={handleChangeTab}
            indicatorColor="primary"
            textColor="inherit"
            variant="fullWidth"
            sx={{
              '& .MuiTabs-indicator': {
                height: 4,
                borderRadius: 2
              }
            }}
          >
            <Tab 
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Description fontSize="small" />
                  <span>Overview</span>
                </Stack>
              } 
              sx={{ fontSize: '1rem', py: 2 }} 
            />
            <Tab 
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Chat fontSize="small" />
                  <span>Brand Chat</span>
                </Stack>
              } 
              sx={{ fontSize: '1rem', py: 2 }} 
            />
          </Tabs>
        </AppBar>

        <DialogContent sx={{ flex: 1, p: 3 }}>
          {isUpdatingStatus ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress size={64} thickness={2} />
            </Box>
          ) : (
            <>
              <TabPanel value={tabIndex} index={0}>
                <Stack spacing={3}>
                  <StyledPaper>
                    <Stack spacing={2}>
                      <Typography variant="h5" fontWeight="600">
                        <AttachMoney color="action" sx={{ verticalAlign: 'middle', mr: 1 }} />
                        Campaign Details
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        <Box>
                          <Typography variant="subtitle1" color="text.secondary">
                            Ideal Due Date
                          </Typography>
                          <Typography variant="h6" fontWeight="600">
                            {formatIdealDueDate(campaign.ideal_duedate)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle1" color="text.secondary">
                            Current Status
                          </Typography>
                          <Chip 
                            label={creator?.status || campaign.campaign_status} 
                            color={
                              creator?.status === 'Accepted' ? 'success' :
                              creator?.status === 'Declined' ? 'error' : 'info'
                            }
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                    </Stack>
                  </StyledPaper>

                  <StyledPaper>
                    <Typography variant="h5" fontWeight="600" gutterBottom>
                      <Description color="action" sx={{ verticalAlign: 'middle', mr: 1 }} />
                      Campaign Brief
                    </Typography>
                    <Card variant="outlined" sx={{ p: 2, backgroundColor: theme.palette.grey[50] }}>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre', lineHeight: 1.6 }}>
                        {formatLinksInMessage(campaign.brief)}
                      </Typography>
                    </Card>
                  </StyledPaper>

                  {creator && (
                    <StyledPaper>
                      <Stack spacing={3}>
                        <Box>
                          <Typography variant="h5" fontWeight="600" gutterBottom>
                            <SwapHoriz color="action" sx={{ verticalAlign: 'middle', mr: 1 }} />
                            Creator Proposal
                          </Typography>
                          
                          <Box sx={{ 
                            backgroundColor: theme.palette.grey[100],
                            borderRadius: 2,
                            p: 2,
                            mb: 2
                          }}>
                            <Typography variant="subtitle1" color="text.secondary">
                              Negotiated Rate
                            </Typography>
                            <Typography variant="h3" fontWeight="700" color="success.main">
                              ${currentRate?.toFixed(2)}
                            </Typography>
                          </Box>

                          <Stack spacing={2}>
                            <Box>
                              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                                Promotion Details
                              </Typography>
                              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                                {videoList.map(video => (
                                  <Chip
                                    key={`video-${video.id}`}
                                    icon={<CheckCircle fontSize="small" />}
                                    label={`${video.promotionPlatform} - ${video.promotionType}`}
                                    color="primary"
                                    variant="outlined"
                                    sx={{ fontWeight: 500 }}
                                  />
                                ))}
                              </Stack>
                            </Box>

                            <Box>
                              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                                Crosspost Platforms
                              </Typography>
                              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                                {crosspostList.map(video => (
                                  <Chip
                                    key={`cross-${video.id}`}
                                    icon={<Warning fontSize="small" />}
                                    label={`${video.promotionPlatform}`}
                                    color="secondary"
                                    variant="outlined"
                                    sx={{ fontWeight: 500 }}
                                  />
                                ))}
                              </Stack>
                            </Box>
                          </Stack>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box>
                          <Typography variant="h6" fontWeight="600" gutterBottom>
                            Creator Instructions
                          </Typography>
                          <TextField
                            fullWidth
                            variant="filled"
                            value={creator?.postingInstructions || "No instructions provided"}
                            disabled
                            multiline
                            minRows={3}
                            InputProps={{
                              sx: {
                                borderRadius: 2,
                                backgroundColor: theme.palette.grey[50]
                              }
                            }}
                          />
                        </Box>
                      </Stack>
                    </StyledPaper>
                  )}
                </Stack>
              </TabPanel>

              <TabPanel value={tabIndex} index={1}>
                <StyledPaper sx={{ height: '100%' }}>
                  <Typography variant="h5" fontWeight="600" gutterBottom>
                    <Chat color="action" sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Brand Communication
                  </Typography>
                  {isConversationLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                      <CircularProgress size={64} thickness={2} />
                    </Box>
                  ) : conversation?.conversation_id ? (
                    <Box sx={{ 
                      height: '60vh',
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      overflow: 'hidden'
                    }}>
                      <ChatScreen
                        conversationId={conversation.conversation_id}
                        specificReaderId={username}
                        sx={{ height: '100%' }}
                        showWarning={false}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center', 
                      height: '200px',
                      border: `2px dashed ${theme.palette.divider}`,
                      borderRadius: 2
                    }}>
                      <Typography variant="body1" color="text.secondary">
                        No conversation started yet
                      </Typography>
                    </Box>
                  )}
                </StyledPaper>
              </TabPanel>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Stack direction="row" spacing={2} width="100%">
            <Button 
              onClick={handleCloseDialog} 
              variant="outlined" 
              color="inherit"
              sx={{ 
                borderRadius: 2,
                px: 4,
                borderWidth: 2,
                '&:hover': { borderWidth: 2 }
              }}
            >
              Close
            </Button>
            <Button
              variant="contained"
              onClick={() => onClickOption(true)}
              color="success"
              disabled={isUpdatingStatus || creator?.status === "Counter"}
              startIcon={<CheckCircle />}
              sx={{ 
                borderRadius: 2,
                px: 4,
                boxShadow: theme.shadows[2],
                '&:hover': { boxShadow: theme.shadows[4] }
              }}
            >
              Accept
            </Button>
            <Button
              variant="contained"
              onClick={() => setOpenNegotiateDialog(true)}
              color="warning"
              disabled={isUpdatingStatus || creator?.status === "Counter"}
              startIcon={<SwapHoriz />}
              sx={{ 
                borderRadius: 2,
                px: 4,
                boxShadow: theme.shadows[2],
                '&:hover': { boxShadow: theme.shadows[4] }
              }}
            >
              Negotiate
            </Button>
            <Button
              variant="contained"
              onClick={() => onClickOption(false)}
              color="error"
              disabled={isUpdatingStatus || creator?.status === "Counter"}
              startIcon={<Cancel />}
              sx={{ 
                borderRadius: 2,
                px: 4,
                boxShadow: theme.shadows[2],
                '&:hover': { boxShadow: theme.shadows[4] }
              }}
            >
              Reject
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      {campaign && (
        <AgencyNegotiateDialog
          open={openNegotiateDialog}
          handleClose={() => setOpenNegotiateDialog(false)}
          campaign={campaign}
          creatorId={username}
          currentRate={currentRate}
          refreshCampaign={() => queryClient.invalidateQueries(["campaigns"])}
          onSuccess={() => handleCloseDialog()}
        />
      )}

      <AlertDialog alertState={dialogState} />
    </>
  );
};

export default AgencyCampaignPitchDialog;